import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import useApi from "storybook-dashboard/utils/fetching";
import useSWR, { mutate } from "swr";
import Im from "immutable";
import DataTable from "react-data-table-component";
import { getByPathWithCommonPrefix } from "../notifications/metrics";
import { useTextData, useTextPostCache } from "storybook-dashboard/utils/fetching";
import Papa from "papaparse";
import Moment from "moment";
import { BasicSpinner } from "storybook-dashboard/spinners/basicSpinner";

const getColumns = (fields) => {
  let dateFields = Im.fromJS(fields)
    ?.filter((i) => Moment(i.split("to")[0]).isValid())
    ?.sortBy((i) => Moment(i).toISOString())
    ?.reverse();

  console.log("getColumns", fields, dateFields?.toJS());

  let periodColumns = dateFields
    ?.map((date) => ({
      name: <span>{date}</span>,
      selector: (row) => row.getIn(["interactionsByReportPeriod", date]),
      sortable: true,
    }))
    ?.toJS();

  return [
    {
      name: <span>Metric Name</span>,
      selector: (row) => row.getIn(["metric", "name"]),
      sortable: true,
      width: "300px",
      wrap: true,
    },
    {
      name: <span>Number of reports/interactions</span>,
      selector: (row) => row.get("num_reports"),
      sortable: true,
    },
    {
      name: <span>All Time</span>,
      selector: (row) => row.get("totalInteractions"),
      sortable: true,
    },
    ...periodColumns,
  ];
};

const getPeriodsWithValues = (reportingPackageValues) => {
  return reportingPackageValues.filter((value, key) => Moment(key.split("to")[0]).isValid() && !!value).keySeq();
};

const aggregateInteractions = (inputsByReportPackageId) => {
  return inputsByReportPackageId
    .toList()
    .reduce(
      (agg, reportingPackageValues) =>
        getPeriodsWithValues(reportingPackageValues).reduce((a, period) => a.set(period, a.get(period, 0) + 1), agg),
      Im.Map()
    );
};

export default function MetricsReportingTablePage() {
  let { _companyId } = useParams();
  const [metricCompleteData, setMetricCompleteData] = useState();
  const [interactionData, setInteractionData] = useState();
  const [metricNodes, setMetricNodes] = useState();
  const [columns, setColumns] = useState();

  // "_nodes" is a list of all unique base metrics under this company / business unit level
  let { data: _nodes } = useApi("/api/tracker/node/?company_id={_companyId}", {
    _companyId,
  });
  // let { data: metricReportCount } = useApi("/api/dashboard/company/{_companyId}/report/metriccount", {
  //   _companyId,
  // });
  let { data: inputs } = useTextPostCache(
    "/api/company/{_companyId}/dispatch/",
    {
      _companyId,
    },
    { type: "INPUTS_TO_CSV" }
  );

  useEffect(() => {
    if (inputs && metricNodes) {
      let _data = Papa.parse(inputs, { header: true, skipEmptyLines: true });

      let metricNodeIds = Im.Set(metricNodes.map((i) => i.getIn(["metric", "uid"])));

      // Index the input data for O(1) lookup
      let inputInteractionSummary = _data.data
        .filter((i) => metricNodeIds.has(i.metric_id))
        .reduce((a, i) => a.setIn([i.metric_id, i.reporting_package_id], Im.fromJS(i)), Im.Map())
        .map((i) => aggregateInteractions(i));

      console.log("MetricsReportingTablePage-useEffect", metricNodeIds?.toJS(), inputInteractionSummary?.toJS());

      let _inputData = metricNodes.map((i) => {
        let metric_id = i.getIn(["metric", "uid"]);
        let interactionsByReportPeriod = inputInteractionSummary.get(metric_id, Im.Map());
        return i.set("interactionsByReportPeriod", interactionsByReportPeriod).set(
          "totalInteractions",
          interactionsByReportPeriod.reduce((a, value) => a + value, 0)
        );
      });

      setInteractionData(_inputData);
      setColumns(getColumns(_data.meta.fields));
    }
  }, [inputs, metricNodes]);

  useEffect(() => {
    if (_nodes) {
      let nodesByPath = getByPathWithCommonPrefix(_nodes);

      let visibleMetricNodes = nodesByPath
        .toList()
        .filter((i) => i.get("_type") === "metricscore" && i.getIn(["meta_json", "hidden"]) !== true);

      console.log("MetricsReportingTablePage-useEffect-nodes", visibleMetricNodes?.toJS());

      setMetricNodes(visibleMetricNodes);
    }
  }, [_nodes]);

  if (!(columns && metricNodes && interactionData)) return <BasicSpinner />;

  console.log("MetricsReportingTablePage-DataTable", _companyId, metricNodes?.toJS(), interactionData?.toJS());

  return (
    <div className="container">
      <h4>Data Analysis</h4>
      <DataTable columns={columns} data={interactionData?.toArray()} />
    </div>
  );
}
